/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { useCallback } from 'react';
import { SerializedStyles, css } from '@emotion/react';
import FloatingContent from '../FloatingContent';
import { Column } from '../flexbox';
import { MenuEntry } from './menuEntry';
import { DROP_SHADOW, MediumText, UNSELECTABLE_TEXT } from '../../styles/styles';
import { BLACK, COOL_BLUE, COOL_GREY, WHITE } from '../../styles/colors';
import * as FoV from '../../language/funcOrValue';
import { isNilOrEmpty } from '../../language/module';
import { Link } from 'react-router-dom';
import { ReactKey } from '../reactKey';
import { VerticalPosition } from '../position/verticalPosition';
import { HorizontalPosition } from '../position/horizontalPosition';
import { useRefCallback } from '../../language/hooks/useRefCallback';
import { useThrottleState } from '../../language/hooks/useThrottleState';
import { getResource } from '../../language/resources/getResource';
import { IAppState } from '../../core/redux/IAppState';
import { useSelector } from 'react-redux';


const menuEntryStyle = css`
  color: ${BLACK};
  ${MediumText};
  padding: 10px 20px;
  text-decoration: none;
  text-underline: none;

  &:hover {
    background-color: ${COOL_BLUE};
    cursor: pointer;
  }

  ${UNSELECTABLE_TEXT};
`;


const floatingMenuColumn = (menuCss?: SerializedStyles) =>
  css([
    css`
      background-color: ${WHITE};
      width: 250px;
      margin-top: 8px;
      border: 1px solid ${COOL_GREY};
      ${DROP_SHADOW};
    `,
    menuCss
  ]);


type Props = {
  menuItems: MenuEntry[];
  children: React.ReactNode;
  menuTitle?: (isOpen: boolean) => string;
  menuCss?: SerializedStyles;
  innerCss?: SerializedStyles;
  rootCss?: SerializedStyles;
  xPosition?: HorizontalPosition;
  yPosition?: VerticalPosition;
  disabled?: boolean;
  menuZIndex?: number;
}


/**
 * FloatingMenu: a component that accepts a trigger view (usually an image) to be clicked to open/close a context menu
 * that "floats" over the trigger content. The menu's items can be configured in all sorts of ways if you see
 * MenuEntry.ts. You'll see menu in use on the Page Header, but it can be used anywhere to make a context menu.
 *
 * Be wary: menu will get cut off by overflow styles, and there's not much that can be done about that.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
const FloatingMenu: React.FC<Props> = props => {
  const [isOpen, setIsOpen] = useThrottleState(false);

  const closeMenu = useCallback(() => setIsOpen(false), [setIsOpen]);
  const resources = useSelector((state: IAppState) => state.locale);

  const onClickToToggleMenu = useRefCallback(
    () => {
      if (props.disabled) {
        return;
      }

      setIsOpen(!isOpen);
    },
  );

  const onItemSelect = (entry: MenuEntry, key: ReactKey) => () => {
    if (entry.onClick) {
      entry.onClick(key);
    }

    console.log('Closing menu due to item selection');
    closeMenu();
  };

  const makeMenuItems = (menuItems: MenuEntry[]): React.ReactNode[] => menuItems.map(
    item => {
      const key  = FoV.unwrap(item.key || FoV.unwrap(item.text));
      const hide = FoV.unwrap(item.hide || false, key);

      if (hide) {
        return null;
      }

      const disable = FoV.unwrap(item.disable || false, key);

      if (disable) {
        return <span
          key={key}
          title={FoV.unwrap(item.title || '', key)}
          css={css`
            opacity: .5;
            ${menuEntryStyle};

            &:hover {
              cursor: not-allowed;
            }
          `}
        >
          {FoV.unwrap(item.text || '', key)}
        </span>;
      }

      const link    = FoV.unwrap(item.href || '');
      const onClick = onItemSelect(item, key);

      if (!isNilOrEmpty(link)) {
        return <Link
          key={key}
          title={FoV.unwrap(item.title || '', key)}
          to={link}
          onClick={onClick}
          css={menuEntryStyle}
        >{FoV.unwrap(item.text || '', key)}</Link>;
      }

      return (
        <span
          key={key}
          title={FoV.unwrap(item.title || '', key)}
          onClick={onClick}
          css={menuEntryStyle}
        >{FoV.unwrap(item.text || '', key)}</span>
      );
    },
  );

  // A disabled trigger opens nothing, so it only shows a title its caller gives, which can say why
  const triggerTitle = props.menuTitle
    ? props.menuTitle(isOpen)
    : props.disabled ? undefined : getResource(resources, isOpen ? 'FLOATING_MENU_CLOSE' : 'FLOATING_MENU_OPEN');

  return (
    <FloatingContent
      showFloatingContent={isOpen}
      xPosition={props.xPosition || 'center'}
      yPosition={props.yPosition || 'below'}
      onClickOutside={closeMenu}
      floatingContent={
        <Column
          rootCss={floatingMenuColumn(props.menuCss)}
        >
          {makeMenuItems(props.menuItems)}
        </Column>
      }
      zIndex={props.menuZIndex}
      rootCss={css`
        height: 100%;
        display: inline-block;
        ${props.rootCss};
      `}
    >
      <div
        onClick={onClickToToggleMenu}
        title={triggerTitle}
        css={css`
          cursor: ${props.disabled ? 'not-allowed' : 'pointer'};

          &:hover {
            ${props.disabled ? '' : 'opacity: .5'};
          }

          ${props.innerCss};
        `}
      >{props.children}</div>
    </FloatingContent>
  );
};


export default FloatingMenu;
